/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useGSAP } from '@gsap/react';
import { graphql, useStaticQuery } from 'gatsby';
import gsap from 'gsap';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS } from '../../../constants';
import { ButtonLinkType } from '../../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { AreaOfExpertise } from '../../../templates/AreaOfExpertisePage';
import { ModuleBgColor } from '../../../templates/Page';
import { Service } from '../../../templates/ServicePage';
import { RawPortableText } from '../../../types/types';
import { getAreasOfExpertiseAsBlocks, getServicesAsBlocks } from '../../../utils/projectUtils';
import { localize } from '../../../utils/sanity';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import ListItem, { ListItemProps } from '../ListItem';
import ModuleLayout from '../ModuleLayout';
import * as styles from './ItemGridModule.module.scss';

export const ItemGridModuleFragment = graphql`
  fragment ItemGridModule on SanityPageItemGridModule {
    title {
      ...LocaleStringFragment
    }
    contentPrepopulationSource
    items {
      title {
        ...LocaleStringFragment
      }
      text {
        ...LocaleTextFragment
      }
      link {
        ...ButtonLink
      }
      icon {
        ...SanityImage
      }
    }
    withSmallIcons
  }
`;

export interface ItemGrid {
  title: string;
  text: string | RawPortableText;
  additionalExplanation?: string;
  link?: ButtonLinkType;
  icon?: SanityImageType;
}

export type ItemGridModuleProps = {
  title: string;
  contentPrepopulationSource?: 'areasOfExpertise' | 'services' | 'none';
  items: Array<ItemGrid>;
  withSmallIcons?: boolean;
  className?: string;
};

interface QueryData {
  allSanityAreaOfExpertise: {
    nodes: Array<AreaOfExpertise>;
  };
  allSanityService: {
    nodes: Array<Service>;
  };
}

export function getModuleBgColor(props: ItemGridModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'white';
}

function ItemGridModule(props: ItemGridModuleProps & CommonModuleProps): React.ReactElement {
  const {
    title,
    contentPrepopulationSource,
    items,
    withSmallIcons,
    className,
    previousModuleBgColor,
    moduleId,
  } = props;
  const queryData = useStaticQuery<QueryData>(graphql`
    {
      allSanityAreaOfExpertise(sort: { orderRank: ASC }) {
        nodes {
          ...AreaOfExpertise
        }
      }
      allSanityService(sort: { orderRank: ASC }) {
        nodes {
          ...Service
        }
      }
    }
  `);

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(queryData, langs);

  const { allSanityAreaOfExpertise, allSanityService } = localizedData;

  const areasOfExpertiseAsItems = getAreasOfExpertiseAsBlocks(
    allSanityAreaOfExpertise.nodes,
  ) as Array<ItemGrid>;

  const servicesAsItems = getServicesAsBlocks(allSanityService.nodes) as Array<ItemGrid>;

  const containerRef = useRef<HTMLElement | null>(null);

  const itemsToUse =
    (contentPrepopulationSource === 'areasOfExpertise' && areasOfExpertiseAsItems) ||
    (contentPrepopulationSource === 'services' && servicesAsItems) ||
    items;

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: '.' + styles.itemsContainer,
          start: 'top bottom',
        } satisfies ScrollTrigger.Vars,
      });

      if (itemsToUse.length > 4) {
        // animate each element
        tl.from(`.${styles.itemContainer} > *`, {
          opacity: 0,
          duration: 0.6,
          scale: 0.1,
          y: 60,

          stagger: {
            amount: 1.5,
            grid: 'auto',
            axis: undefined,
            ease: 'none',
            from: 'start',
          },
        });
      }
    },
    { scope: containerRef },
  );

  return (
    <ModuleLayout
      id={moduleId}
      className={className}
      title={title}
      moduleRef={containerRef}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
      contentClassName={styles.contentContainer}
    >
      <ul
        className={clsx(
          styles.itemsContainer,
          !itemsToUse.find(item => item.icon) && styles.withNoIcon,
          itemsToUse.find(item => item.icon) && !withSmallIcons && styles.withLargeIcons,
          itemsToUse.find(item => item.icon) && withSmallIcons && styles.withSmallIcons,
          (itemsToUse.length === 3 ||
            itemsToUse.length === 5 ||
            itemsToUse.length === 6 ||
            itemsToUse.length === 12) &&
            styles.threeInRow,
          (itemsToUse.length === 4 || (itemsToUse.length >= 7 && itemsToUse.length !== 12)) &&
            styles.fourInRow,
        )}
      >
        {itemsToUse.map((item, i) => {
          const listType = item.icon ? 'withImage' : 'withoutMarkers';

          const listItemProps = {
            listType,
            text: item.text,
            title: item.title,
            additionalExplanation: item.additionalExplanation,
            link: item.link,
            ...(listType === 'withImage' && item.icon
              ? { imageSize: withSmallIcons ? 'small' : 'large', image: item.icon }
              : {}),
          } as ListItemProps;

          return (
            <React.Fragment key={i}>
              <ListItem {...listItemProps} className={clsx(styles.itemContainer)} />
              <li className={styles.divider}></li>
            </React.Fragment>
          );
        })}
      </ul>
    </ModuleLayout>
  );
}

export default ItemGridModule;
